import { homedir } from 'node:os';
import { join } from 'node:path';
import { pipeline, env, type FeatureExtractionPipeline } from '@huggingface/transformers';
import { DEFAULT_MODEL_ID, getModelConfig, getConfiguredModelId } from '../models/registry.js';
import type { EmbeddingConfig } from '../types/config.js';

// Set cache directory to ~/.cache/huggingface-transformers (outside node_modules)
// This allows CI caching and prevents model re-downloads on each npm install
env.cacheDir = join(homedir(), '.cache', 'huggingface-transformers');

/**
 * Check if a fine-tuned model path is configured via BK_FINETUNED_MODEL.
 * This takes priority over BK_MODEL for using locally fine-tuned models.
 */
export function getFinetunedModelPath(): string | undefined {
  const path = process.env['BK_FINETUNED_MODEL'];
  if (path !== undefined && path !== '') {
    return path;
  }
  return undefined;
}

/**
 * Build EmbeddingConfig from a model ID, using registry defaults.
 *
 * @param modelId - Short model ID (e.g., "bge-small-en-v1.5") or full HuggingFace ID
 * @param overrides - Optional overrides for specific config values
 */
export function buildEmbeddingConfig(
  modelId: string,
  overrides?: Partial<Omit<EmbeddingConfig, 'model'>>
): EmbeddingConfig {
  // Env var overrides for controlled benchmark testing (BK_POOLING, BK_QUERY_PREFIX)
  const rawPooling = process.env['BK_POOLING'];
  const envPooling =
    rawPooling === 'mean' || rawPooling === 'cls' || rawPooling === 'none' ? rawPooling : undefined;
  const envQueryPrefix = process.env['BK_QUERY_PREFIX'];

  const modelConfig = getModelConfig(modelId);
  if (modelConfig === undefined) {
    // Unknown model (e.g. fine-tuned local path) — try inheriting from BK_BASE_MODEL
    const baseModelId = process.env['BK_BASE_MODEL'];
    const baseConfig =
      baseModelId !== undefined && baseModelId !== '' ? getModelConfig(baseModelId) : undefined;
    return {
      model: modelId,
      batchSize: overrides?.batchSize ?? 32,
      dtype: overrides?.dtype ?? 'fp32',
      pooling: overrides?.pooling ?? envPooling ?? baseConfig?.pooling ?? 'mean',
      normalize: overrides?.normalize ?? baseConfig?.normalize ?? true,
      queryPrefix: overrides?.queryPrefix ?? envQueryPrefix ?? baseConfig?.queryPrefix ?? '',
      docPrefix: overrides?.docPrefix ?? baseConfig?.docPrefix ?? '',
      maxInFlightBatches: overrides?.maxInFlightBatches ?? 1,
    };
  }

  return {
    model: modelConfig.id,
    batchSize: overrides?.batchSize ?? 32,
    dtype: overrides?.dtype ?? 'fp32',
    pooling: overrides?.pooling ?? envPooling ?? modelConfig.pooling,
    normalize: overrides?.normalize ?? modelConfig.normalize,
    queryPrefix: overrides?.queryPrefix ?? envQueryPrefix ?? modelConfig.queryPrefix,
    docPrefix: overrides?.docPrefix ?? modelConfig.docPrefix,
    maxInFlightBatches: overrides?.maxInFlightBatches ?? 1,
  };
}

/**
 * Default embedding configuration for tests and standalone usage.
 * Uses the registry default model with its recommended settings.
 * Production code should use AppConfig.embedding from config.service.
 *
 * Override priority:
 * 1. BK_FINETUNED_MODEL - Local path to fine-tuned ONNX model (highest priority)
 * 2. BK_MODEL or BK_EMBEDDING_MODEL - HuggingFace model ID from registry
 * 3. Default model (snowflake-arctic-embed-s)
 *
 * When using BK_FINETUNED_MODEL, set BK_BASE_MODEL to inherit pooling/prefix
 * from a registry model (e.g. BK_BASE_MODEL=snowflake-arctic-embed-s).
 */
export const DEFAULT_EMBEDDING_CONFIG: EmbeddingConfig = buildEmbeddingConfig(
  getFinetunedModelPath() ?? getConfiguredModelId()
);

/**
 * Get the default model's HuggingFace ID.
 */
export function getDefaultModelId(): string {
  const config = getModelConfig(DEFAULT_MODEL_ID);
  return config?.id ?? 'Snowflake/snowflake-arctic-embed-s';
}

export class EmbeddingEngine {
  private extractor: FeatureExtractionPipeline | null = null;
  private initPromise: Promise<void> | null = null;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly -- mutated in embed() and embedBatch()
  private _dimensions: number | null = null;
  // eslint-disable-next-line @typescript-eslint/prefer-readonly -- mutated in dispose()
  private disposed = false;
  private readonly config: EmbeddingConfig;

  constructor(config: EmbeddingConfig = DEFAULT_EMBEDDING_CONFIG) {
    if (process.env['BK_DEBUG'] !== undefined && process.env['BK_DEBUG'] !== '') {
      // eslint-disable-next-line no-console
      console.log('[EmbeddingEngine] Using model:', config.model);
    }
    this.config = config;
  }

  /**
   * Guard against use-after-dispose
   */
  private assertNotDisposed(): void {
    if (this.disposed) {
      throw new Error('EmbeddingEngine has been disposed');
    }
  }

  /**
   * Initialize the embedding pipeline (concurrency-safe).
   * Multiple concurrent calls will share the same initialization promise.
   */
  async initialize(): Promise<void> {
    this.assertNotDisposed();
    if (this.extractor !== null) return;

    this.initPromise ??= (async (): Promise<void> => {
      try {
        // @ts-expect-error TS2590: TypeScript can't represent the complex union type from pipeline()
        // This is a known limitation with @huggingface/transformers overloaded signatures
        this.extractor = await pipeline('feature-extraction', this.config.model, {
          dtype: this.config.dtype,
        });
      } catch (error) {
        // Allow retry on failure
        this.initPromise = null;
        throw error;
      }
    })();
    await this.initPromise;
  }

  /**
   * Embed a search query. Applies queryPrefix for asymmetric models.
   */
  async embedQuery(text: string): Promise<Float32Array> {
    return this.embedText(this.config.queryPrefix + text);
  }

  /**
   * Embed a document for indexing. Applies docPrefix for asymmetric models.
   */
  async embedDocument(text: string): Promise<Float32Array> {
    return this.embedText(this.config.docPrefix + text);
  }

  /**
   * Internal: embed text without prefix.
   */
  private async embedText(text: string): Promise<Float32Array> {
    this.assertNotDisposed();
    if (this.extractor === null) {
      await this.initialize();
    }
    if (this.extractor === null) {
      throw new Error('Failed to initialize embedding model');
    }
    const output = await this.extractor(text, {
      pooling: this.config.pooling,
      normalize: this.config.normalize,
    });
    // Return Float32Array directly (no conversion overhead)
    const dim = output.dims[output.dims.length - 1] ?? 0;
    this._dimensions ??= dim;
    return Float32Array.from(output.data);
  }

  /**
   * Embed a batch of documents with optional parallelism.
   * When maxInFlightBatches > 1, processes multiple batches concurrently.
   */
  async embedBatch(texts: string[]): Promise<Float32Array[]> {
    this.assertNotDisposed();
    if (this.extractor === null) {
      await this.initialize();
    }
    if (this.extractor === null) {
      throw new Error('Failed to initialize embedding model');
    }

    // Split texts into batches
    const batches: string[][] = [];
    for (let i = 0; i < texts.length; i += this.config.batchSize) {
      batches.push(texts.slice(i, i + this.config.batchSize));
    }

    if (batches.length === 0) {
      return [];
    }

    // Process batches
    if (this.config.maxInFlightBatches <= 1) {
      // Sequential processing (default)
      return this.embedBatchesSequential(batches);
    } else {
      // Concurrent processing with controlled parallelism
      return this.embedBatchesConcurrent(batches);
    }
  }

  /**
   * Process batches sequentially (original behavior).
   */
  private async embedBatchesSequential(batches: string[][]): Promise<Float32Array[]> {
    const results: Float32Array[] = [];

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      if (batch === undefined) continue;
      const batchResults = await this.processSingleBatch(batch);
      results.push(...batchResults);

      // Yield event loop between batches
      if (i < batches.length - 1) {
        await new Promise((resolve) => setImmediate(resolve));
      }
    }

    return results;
  }

  /**
   * Process batches with controlled concurrency.
   */
  private async embedBatchesConcurrent(batches: string[][]): Promise<Float32Array[]> {
    const results: Float32Array[][] = new Array<Float32Array[]>(batches.length);
    let inFlight = 0;
    const maxConcurrent = this.config.maxInFlightBatches;

    // Process all batches with semaphore-style concurrency control
    await Promise.all(
      batches.map(async (batch, idx) => {
        // Wait for a slot
        while (inFlight >= maxConcurrent) {
          await new Promise((resolve) => setImmediate(resolve));
        }
        inFlight++;

        try {
          results[idx] = await this.processSingleBatch(batch);
        } finally {
          inFlight--;
        }
      })
    );

    // Flatten results in order
    return results.flat();
  }

  /**
   * Process a single batch and return embeddings.
   */
  private async processSingleBatch(batch: string[]): Promise<Float32Array[]> {
    if (this.extractor === null) {
      throw new Error('Extractor not initialized');
    }

    // Apply document prefix to each text in batch
    const prefixedBatch = batch.map((text) => this.config.docPrefix + text);

    const output = await this.extractor(prefixedBatch, {
      pooling: this.config.pooling,
      normalize: this.config.normalize,
    });

    // Unpack batch results from tensor
    const dim = output.dims[output.dims.length - 1] ?? 0;
    const batchResults: Float32Array[] = [];

    for (let b = 0; b < batch.length; b++) {
      const start = b * dim;
      const end = start + dim;
      batchResults.push(Float32Array.from(output.data.slice(start, end)));
    }

    // Cache dimensions
    this._dimensions ??= dim;

    return batchResults;
  }

  /**
   * Get cached embedding dimensions. Throws if embed() hasn't been called yet.
   * Use ensureDimensions() if you need to guarantee dimensions are available.
   */
  getDimensions(): number {
    if (this._dimensions === null) {
      throw new Error('Cannot get dimensions before first embed() call');
    }
    return this._dimensions;
  }

  /**
   * Check if the embedding pipeline is initialized.
   */
  isInitialized(): boolean {
    return this.extractor !== null;
  }

  /**
   * Check if this engine has been disposed.
   */
  isDisposed(): boolean {
    return this.disposed;
  }

  /**
   * Reset the engine to uninitialized state, allowing reuse after disposal.
   * If currently initialized, disposes the pipeline first.
   */
  async reset(): Promise<void> {
    if (this.extractor !== null) {
      await this.extractor.dispose();
      this.extractor = null;
    }
    this.initPromise = null;
    this._dimensions = null;
    this.disposed = false;
  }

  /**
   * Ensure dimensions are available, initializing the model if needed.
   * Returns the embedding dimensions for the current model.
   */
  async ensureDimensions(): Promise<number> {
    if (this._dimensions === null) {
      // Use non-empty probe string (more reliable than empty string)
      await this.embedText('dimension probe');
    }
    if (this._dimensions === null) {
      throw new Error('Failed to determine embedding dimensions');
    }
    return this._dimensions;
  }

  /**
   * Dispose the embedding pipeline to free resources.
   * Should be called before process exit to prevent ONNX runtime cleanup issues on macOS.
   * After disposal, this engine cannot be used again.
   */
  async dispose(): Promise<void> {
    if (this.extractor !== null) {
      await this.extractor.dispose();
      this.extractor = null;
    }
    this.initPromise = null;
    this._dimensions = null;
    this.disposed = true;
  }
}
